import { createFileRoute } from "@tanstack/react-router";
import { getOS, OS } from "common/utils/context";
import { enumValues } from "common/utils/enum";
import perfNow from "common/utils/perfNow";
import { EnvPreset } from "gfx/EnvPreset";
import { estimateHeightMapGPUMemory, forgetCachedHeightmaps } from "gfx/materials/baking/heightMapFromDesignMaterial";
import { TEXTURE_ATLAS_TEXTURE_WIDTH } from "gfx/materials/textureAtlasConstants";
import { usePreloadedMaterialTextures } from "gfx/materials/usePreloadedMaterialTextures";
import { formatBytes } from "gfx/MeshDataStats";
import PerspectiveControls from "gfx/PerspectiveControls";
import { envPresetSceneLights } from "gfx/sceneLights";
import { MaterialSharedParameterGroup } from "gfx/threejs/higharc/MaterialSharedParameterGroup";
import THREEViewer from "gfx/THREEViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type { StressTestTextures } from "test/util/materialStressTestUtil";
import { materialStressTests, renderTestMaterials, MaterialStressTestType } from "test/util/materialStressTestUtil";
import type * as THREE from "three";
import withAuthentication from "../../../components/withAuthentication";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 2000,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 5000,
};

function MaterialStressTestPage() {
  const [viewerReady, setViewerReady] = useState<boolean>(false);
  const testTexturesRef = useRef<StressTestTextures | undefined>(undefined);

  const materialList = useRef<THREE.Material[]>([]);
  const manuallyAccountedMemory = useRef<number>(0);

  const [materialCount, setMaterialCount] = useState<number>(0);
  const [gpuMemoryCounter, setGpuMemoryCounter] = useState<number>(0);
  const [lastPerformanceResult, setLastPerformanceResult] = useState<number>(0);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const manualAllocations = useRef<THREE.Texture[]>([]);

  const { fetchedTextures, texturesReady } = usePreloadedMaterialTextures();
  const displayMeshesRef = useRef<THREE.Mesh[] | undefined>(undefined);

  async function runTest() {
    MaterialSharedParameterGroup.instance.sharedDefines.parallaxEffectEnabled = getOS() !== OS.IPHONE;

    const viewer = viewerRef.current;
    const textures = fetchedTextures.current;
    if (!viewer || !textures) {
      return;
    }
    const renderer = viewer.viewerRenderer.renderer;

    const t0 = perfNow();

    removeMeshes();
    await forgetCachedHeightmaps(renderer);
    materialList.current = [];
    manuallyAccountedMemory.current = 0;
    for (const texture of manualAllocations.current) {
      texture.dispose();
    }
    manualAllocations.current = [];

    for (const testType of enumValues(MaterialStressTestType)) {
      const test = materialStressTests[testType];

      const generatedCount = test.automatedStressTestCount;
      const startParamsIndex = 0;
      const { materials: newMaterials, manuallyAccountedMemoryBytes: memoryBytes } = await test.generateMaterials(
        startParamsIndex,
        generatedCount,
        { manualAllocations, testTexturesRef, viewer, textures },
      );

      materialList.current.push(...newMaterials);
      manuallyAccountedMemory.current += memoryBytes;
    }

    removeMeshes();
    displayMeshesRef.current = await renderTestMaterials(viewer.scene, materialList.current);

    viewer.viewerRenderer?.forceRenderAndFinish();
    const t1 = perfNow();
    const measurement = t1 - t0;
    console.log("Last performance test result: " + measurement + " milliseconds");
    setLastPerformanceResult(measurement);
    setMaterialCount(materialList.current.length);

    const textureAtlasMemory =
      (testTexturesRef.current?.length() ?? 0) * TEXTURE_ATLAS_TEXTURE_WIDTH * TEXTURE_ATLAS_TEXTURE_WIDTH;
    setGpuMemoryCounter(estimateHeightMapGPUMemory(renderer) + manuallyAccountedMemory.current + textureAtlasMemory);
  }

  useEffect(() => {
    // Note that not loading the env map will result in the placeholder env map being used.
    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.WHITEBOX],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    const viewer = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
      },
    });

    void (async () => {
      if (viewer.performanceTierPromise) {
        await viewer.performanceTierPromise;
      }
      viewerRef.current = viewer;
      setViewerReady(true);
    })();

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  function removeMeshes() {
    const displayMeshes = displayMeshesRef.current;
    const viewer = viewerRef.current;
    if (displayMeshes === undefined || !viewer) {
      return;
    }
    for (let meshIndex = 0; meshIndex < displayMeshes.length; ++meshIndex) {
      const mesh = displayMeshes[meshIndex];
      viewer.scene.remove(mesh);
    }
    displayMeshesRef.current = [];
  }

  return (
    <div>
      <Head>
        <title>{getPageTitle("Material Load Stress Test")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
          body {
              width:  100%;
              height: 100%;
              margin: 0;
              padding: 0;
          }

          .row, .col {  overflow: hidden; position: absolute; }
          .row { left: 0; right: 0; }
          .col { top: 0; bottom: 0; }

          .button-container2 {
            z-index: 100;
            position: absolute;
            bottom: 0;
            left: 0;
            padding: 10px;
          }

          button {
              display: inline-block;
              padding: 5px;
              margin: 2px !important;
          }
        `}</style>
      </Head>

      <div className="row col" ref={viewerContainerRef} />
      <div className="button-container2">
        <button
          onClick={() => {
            const viewer = viewerRef.current;
            if (!viewer) {
              return;
            }
            viewer.setCameraSettings(defaultCameraSettings);
            const cameraControls = new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0);
            viewer.setControls(cameraControls);
          }}
        >
          Reset Camera
        </button>
        {viewerReady && texturesReady ? (
          <button
            onClick={() => {
              if (!viewerRef.current || !fetchedTextures.current) {
                return;
              }
              void runTest();
            }}
          >
            Run Test
          </button>
        ) : (
          ""
        )}
        <div>This version of the material loading stress test is meant to be run automatically.</div>
        <div>
          Number of materials: {materialCount}. Estimated GPU memory in use for textures:{" "}
          {formatBytes(gpuMemoryCounter)} Number of textures: {testTexturesRef.current?.length() ?? 0}
        </div>
        <div>
          Latest load time measurement: <span id="loadTimeMeasurement">{lastPerformanceResult.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/debug/gfx-stress/material-stress")({
  component: withAuthentication(MaterialStressTestPage, { superAdmin: true }),
});
